import type {ReactElement} from 'react';
import React from 'react';
import {Text, TextSize} from '@Components/text';
import {convertSecondsToMMSSFormat} from '@Utils/date.util';
import {CurrentTimeText} from '@Components/poster-editor/components/poster-seekbar/components/current-time-text';
import {getCurrentPageDuration, getCurrentPageTime, getIsCurrentPagePlaying, isScaleAnimatingToFitToScreen} from '@Components/poster-editor/poster-editor-reducer';
import {onPosterPause, onPosterPlay} from '@Components/poster-editor/library/poster-editor-library';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {PlayPauseButton} from '@Components/media-control-buttons/components/play-pause-button';
import {useAppSelector} from '@/hooks';
import styles from './poster-mobile-small-seekbar.module.scss';
import useWhiteboardContainerDimensions from '@Hooks/poster-editor/useWhiteboardContainerDimensions';
import {POSTER_MOBILE_BIG_SEEK_BAR_HEIGHT} from '@Components/poster-editor/components/poster-mobile-big-seekbar/poster-mobile-big-seekbar.types';
import {useIsCurrentPageVideo} from '@Hooks/poster-editor/useIsCurrentPageVideo';
import usePosterDimensions from '@Hooks/poster-editor/usePosterDimensions';

export function PosterMobileSmallSeekbar(): ReactElement<any> | null {
  const currentPageDuration = useAppSelector(getCurrentPageDuration);
  const isCurrentPageVideo = useIsCurrentPageVideo();
  const isPlaying = useAppSelector(getIsCurrentPagePlaying);
  const currentTime = useAppSelector(getCurrentPageTime);
  const isScaleAnimatingToFit = useAppSelector(isScaleAnimatingToFitToScreen);
  const posterDimension = usePosterDimensions();
  const whiteboardContainerDimensions = useWhiteboardContainerDimensions();
  const isVersionHistoryModeActive = useAppSelector((state) => {
    return state.posterEditor.isVersionHistoryModeActive;
  });
  const isTimelineModeActive = useAppSelector((state) => {
    return state.posterEditor.isTimelineModeActive;
  });

  const shouldShowSmallMobileSeekbar = (): boolean => {
    if(isTimelineModeActive){
      return false;
    }

    return !isScaleAnimatingToFit && posterDimension.height + POSTER_MOBILE_BIG_SEEK_BAR_HEIGHT > whiteboardContainerDimensions.height && !isVersionHistoryModeActive;
  };

  if(!isCurrentPageVideo || !shouldShowSmallMobileSeekbar()){
    return null
  }

  const getTotalTime = (): ReactElement<any> => {
    return <Text val={convertSecondsToMMSSFormat(currentPageDuration)} size={TextSize.XXSMALL} />;
  };

  const getCurrentTime = (): ReactElement<any> => {
    return <CurrentTimeText />;
  };

  return (
    <div
      className={`${styles.seekbar} radius-round border-s-standard  flexbox spacing-p-2`}
      style={{
        top: `${whiteboardContainerDimensions.bottom - 70}px`,
      }}
    >
      <PlayPauseButton
        className="spacing-p-2"
        isPlaying={isPlaying}
        onPlay={(): void => {
          onPosterPlay();
        }}
        onPause={(): void => {
          onPosterPause();
        }}
        iconShape={IconShape.CIRCLE}
        iconType={IconType.GHOST}
        iconSize={IconSize.SIZE_ICON_24}
        totalDuration={currentPageDuration}
        currentTime={currentTime}
      />
      <div className="flex-center spacing-m-r-3 spacing-m-l-2">
        {getCurrentTime()}
        {getTotalTime()}
      </div>
    </div>
  );
}
